import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, MapPin, CalendarDays, ArrowRight, Edit3 } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { DraftPickerDrawer } from "./DraftPickerDrawer";
import { useLocalizedDates } from "./i18n/dates";
import { localizeTemplateName } from "./i18n/shiftNames";

const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfMonth = (d) => { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x; };

export default function PortalSchedule() {
  const { t } = useTranslation();
  const { months, dateFnsLocale } = useLocalizedDates();
  const today = new Date();
  const [weekStart, setWeekStart] = useState(startOfWeek(today));
  const [selectedDate, setSelectedDate] = useState(toIso(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calOpen, setCalOpen] = useState(false);
  const [allowedTemplates, setAllowedTemplates] = useState([]);
  const [draftDates, setDraftDates] = useState(new Map());
  const [pickerOpen, setPickerOpen] = useState(false);

  const rangeStart = startOfMonth(weekStart);
  const rangeEnd = addDays(rangeStart, 41);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [scheduleRes, allowedRes, draftsRes] = await Promise.all([
          api.get("/portal/schedule", {
            params: { start_date: toIso(rangeStart), end_date: toIso(rangeEnd) },
          }),
          api.get("/portal/shift-drafts/allowed-templates"),
          api.get("/portal/shift-drafts", {
            params: { from: toIso(rangeStart), to: toIso(rangeEnd) },
          }),
        ]);
        setData(scheduleRes.data);
        setAllowedTemplates(allowedRes.data?.items || []);
        setDraftDates(new Map((draftsRes.data?.items || []).map((d) => [d.date, d])));
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || t("common.error"));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart.getTime()]);

  const daysMap = useMemo(() => new Map((data?.days || []).map((d) => [d.date, d])), [data]);
  const selectedDay = daysMap.get(selectedDate);
  const todayIso = toIso(today);

  const daysWithShifts = (data?.days || []).map((d) => {
    const [y, m, dd] = d.date.split("-").map(Number);
    return new Date(y, m - 1, dd);
  });

  // Hours sum for the month containing weekStart (filter days by month prefix)
  const monthStats = useMemo(() => {
    const yyyymm = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, "0")}`;
    let h = 0;
    let shifts = 0;
    for (const [iso, day] of daysMap.entries()) {
      if (iso.startsWith(yyyymm)) {
        h += day.hours;
        shifts += day.shifts.length;
      }
    }
    return { hours: Math.round(h * 10) / 10, shifts };
  }, [weekStart, daysMap]);

  // Workdays × 8h target for displayed month
  const monthTarget = useMemo(() => {
    const y = weekStart.getFullYear();
    const m = weekStart.getMonth();
    const last = new Date(y, m + 1, 0).getDate();
    let work = 0;
    for (let i = 1; i <= last; i++) {
      const dow = new Date(y, m, i).getDay();
      if (dow !== 0 && dow !== 6) work++;
    }
    return work * 8;
  }, [weekStart]);

  const upcoming = useMemo(() => (data?.days || []).filter((d) => d.date >= todayIso).slice(0, 8), [data, todayIso]);

  const isFuture = selectedDate > todayIso;
  const selectedDraft = draftDates.get(selectedDate);
  const canPlanDrafts = allowedTemplates.length > 0;

  const reloadDrafts = async () => {
    try {
      const draftsRes = await api.get("/portal/shift-drafts", {
        params: { from: toIso(rangeStart), to: toIso(rangeEnd) },
      });
      setDraftDates(new Map((draftsRes.data?.items || []).map((d) => [d.date, d])));
    } catch (e) {
      // silent
    }
  };

  return (
    <div className="space-y-6 px-4 pb-8 pt-6 md:px-6 md:pt-8">
      {/* Header */}
      <header className="flex items-end justify-between gap-4">
        <div>
          <p className="text-[14px] text-zinc-500">{t("schedule.title")}</p>
          <h1 className="mt-1 text-[28px] font-semibold tracking-tight md:text-[32px]">
            {weekStart.getFullYear()}. {months[weekStart.getMonth()]}
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-full bg-white p-1 shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
            <button
              type="button"
              data-testid="schedule-prev-week"
              onClick={() => setWeekStart(addDays(weekStart, -7))}
              className="grid h-8 w-8 place-items-center rounded-full text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-white"
              aria-label={t("common.back")}
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={2} />
            </button>
            <button
              type="button"
              onClick={() => { setWeekStart(startOfWeek(new Date())); setSelectedDate(toIso(new Date())); }}
              className="px-2.5 text-[13px] font-medium text-zinc-700 transition hover:text-blue-600 dark:text-zinc-300"
              data-testid="schedule-today"
            >
              {t("common.today")}
            </button>
            <button
              type="button"
              data-testid="schedule-next-week"
              onClick={() => setWeekStart(addDays(weekStart, 7))}
              className="grid h-8 w-8 place-items-center rounded-full text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-white"
              aria-label={t("common.next")}
            >
              <ChevronRight className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>

          <Popover open={calOpen} onOpenChange={setCalOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="schedule-open-calendar"
                className="grid h-10 w-10 place-items-center rounded-full bg-blue-500 text-white shadow-[0_4px_14px_-4px_rgba(59,130,246,0.5)] transition hover:bg-blue-600"
                aria-label={t("schedule.title")}
              >
                <CalendarDays className="h-4 w-4" strokeWidth={2} />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              sideOffset={8}
              className="rounded-3xl border-0 bg-white p-3 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.25)] dark:bg-zinc-900"
              data-testid="schedule-calendar-popover"
            >
              <Calendar
                mode="single"
                locale={dateFnsLocale}
                selected={new Date(selectedDate + "T00:00:00")}
                weekStartsOn={1}
                onSelect={(d) => {
                  if (!d) return;
                  setSelectedDate(toIso(d));
                  setWeekStart(startOfWeek(d));
                  setCalOpen(false);
                }}
                modifiers={{ hasShift: daysWithShifts }}
                modifiersClassNames={{
                  hasShift: "relative font-semibold after:content-[''] after:absolute after:bottom-0.5 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-1 after:rounded-full after:bg-blue-500",
                }}
                classNames={{
                  months: "flex flex-col gap-3 px-2 pb-1 pt-2",
                  caption: "flex items-center justify-center pt-1 relative",
                  caption_label: "text-[13px] font-medium text-zinc-700 dark:text-zinc-200",
                  nav: "flex items-center gap-1",
                  nav_button: "h-7 w-7 grid place-items-center rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 transition dark:hover:bg-zinc-800 dark:hover:text-white",
                  nav_button_previous: "absolute left-1",
                  nav_button_next: "absolute right-1",
                  head_cell: "w-9 text-center text-[11px] font-medium text-zinc-400",
                  cell: "h-9 w-9 text-center p-0",
                  day: "h-9 w-9 inline-flex items-center justify-center rounded-full text-[12.5px] font-medium tabular-nums hover:bg-zinc-100 focus:outline-none dark:hover:bg-zinc-800",
                  day_selected: "bg-blue-500 text-white hover:bg-blue-500 hover:text-white",
                  day_today: "ring-2 ring-blue-500 ring-inset",
                  day_outside: "text-zinc-300 dark:text-zinc-700",
                  day_disabled: "opacity-40",
                  day_hidden: "invisible",
                }}
              />
            </PopoverContent>
          </Popover>
        </div>
      </header>

      {/* Monthly totals */}
      {!loading && (
        <MonthTotals
          hours={monthStats.hours}
          shifts={monthStats.shifts}
          target={monthTarget}
        />
      )}

      {/* Week strip */}
      {loading ? (
        <div className="h-[96px] animate-pulse rounded-3xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900" />
      ) : (
        <WeekStrip
          start={weekStart}
          dayMap={daysMap}
          selectedDate={selectedDate}
          onSelect={setSelectedDate}
        />
      )}

      {/* Selected day */}
      <section>
        <SelectedDayCard date={selectedDate} day={selectedDay} />
        {canPlanDrafts && isFuture && (
          <DraftBanner
            draft={selectedDraft}
            onOpen={() => setPickerOpen(true)}
          />
        )}
      </section>

      {/* Upcoming */}
      <section>
        <div className="mb-3 flex items-end justify-between gap-3 px-1">
          <h2 className="text-[18px] font-semibold tracking-tight">
            {t("schedule.upcoming")}
          </h2>
          {canPlanDrafts && (
            <Link
              to="/dolgozo/tervezet"
              data-testid="schedule-open-drafts"
              className="inline-flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              {t("schedule.my_drafts_link")}
              <ArrowRight className="h-3 w-3" strokeWidth={2} />
            </Link>
          )}
        </div>
        {upcoming.length === 0 ? (
          <div className="rounded-2xl bg-white p-8 text-center shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
            <p className="text-[13.5px] text-zinc-500">{t("dashboard.no_published_shift")}</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] dark:bg-zinc-900">
            {upcoming.map((d, i) => (
              <UpcomingDayRow
                key={d.date}
                day={d}
                selected={d.date === selectedDate}
                onClick={() => {
                  setSelectedDate(d.date);
                  setWeekStart(startOfWeek(new Date(d.date + "T00:00:00")));
                }}
                last={i === upcoming.length - 1}
              />
            ))}
          </div>
        )}
      </section>

      {/* Draft picker drawer */}
      <DraftPickerDrawer
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        date={selectedDate}
        templates={allowedTemplates}
        existingDraft={selectedDraft}
        onSaved={() => {
          setPickerOpen(false);
          reloadDrafts();
        }}
      />
    </div>
  );
}

function DraftBanner({ draft, onOpen }) {
  const { t } = useTranslation();
  if (draft) {
    const tone = draft.status === "approved" ? "emerald" : draft.status === "rejected" ? "rose" : "amber";
    const toneClasses = {
      amber: "bg-amber-50 ring-amber-200 dark:bg-amber-950/30 dark:ring-amber-900/40",
      emerald: "bg-emerald-50 ring-emerald-200 dark:bg-emerald-950/30 dark:ring-emerald-900/40",
      rose: "bg-rose-50 ring-rose-200 dark:bg-rose-950/30 dark:ring-rose-900/40",
    };
    const toneText = {
      amber: "text-amber-800 dark:text-amber-200",
      emerald: "text-emerald-800 dark:text-emerald-200",
      rose: "text-rose-800 dark:text-rose-200",
    };
    const label = draft.status === "approved"
      ? t("schedule.draft_approved_label")
      : draft.status === "rejected"
      ? t("schedule.draft_rejected_label")
      : t("schedule.draft_pending_label");
    return (
      <button
        type="button"
        onClick={onOpen}
        data-testid="schedule-draft-banner"
        className={"mt-3 flex w-full items-center justify-between gap-3 rounded-2xl px-4 py-3 text-left ring-1 transition-colors " + toneClasses[tone]}
      >
        <div className="min-w-0 flex-1">
          <p className={"text-[12.5px] font-semibold " + toneText[tone]}>
            {label} · {localizeTemplateName(draft.template_name, t)}
          </p>
          <p className={"mt-0.5 font-mono text-[11.5px] tabular-nums " + toneText[tone]}>
            {draft.start_time}–{draft.end_time} · {Number(draft.duration_hours || 0).toFixed(1)}h
          </p>
        </div>
        {draft.status === "pending" && (
          <span className="rounded-full bg-white/70 px-2.5 py-1 text-[11px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
            {t("schedule.modify")}
          </span>
        )}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="schedule-create-draft"
      className="mt-3 flex w-full items-center justify-between gap-3 rounded-2xl border-2 border-dashed border-zinc-200 bg-white/40 px-4 py-3 text-left transition-colors hover:border-blue-300 hover:bg-blue-50/40 dark:border-zinc-800 dark:hover:border-blue-700/60 dark:hover:bg-blue-950/20"
    >
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold tracking-tight text-zinc-700 dark:text-zinc-200">
          {t("drafts.open_picker")}
        </p>
        <p className="mt-0.5 text-[11.5px] text-zinc-500">
          {t("schedule.draft_suggestion")}
        </p>
      </div>
      <span className="grid h-9 w-9 place-items-center rounded-full bg-blue-500 text-white shadow-[0_4px_14px_-4px_rgba(59,130,246,0.5)]">
        <Edit3 className="h-4 w-4" strokeWidth={2} />
      </span>
    </button>
  );
}

function MonthTotals({ hours, shifts, target }) {
  const { t } = useTranslation();
  const pct = Math.max(0, Math.min(100, (hours / target) * 100));
  const surplus = hours > target ? hours - target : 0;
  return (
    <div className="rounded-2xl bg-white p-5 shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] dark:bg-zinc-900">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-[13px] text-zinc-500">{t("schedule.month_hours")}</p>
          <p className="mt-1 font-mono text-[30px] font-semibold leading-none tabular-nums tracking-[-0.02em] md:text-[34px]">
            {hours}<span className="ml-1 text-[16px] text-zinc-400">h</span>
          </p>
          <p className="mt-2 text-[12px] text-zinc-500">
            {t("dashboard.shifts_completed", { count: shifts })}
          </p>
        </div>
        <div className="text-right">
          <p className="font-mono text-[15px] font-semibold tabular-nums text-blue-600 dark:text-blue-400">
            {Math.round(pct)}%
          </p>
          <p className="mt-0.5 text-[11.5px] text-zinc-500">
            {surplus > 0
              ? t("dashboard.hours_over", { hours: surplus.toFixed(1) })
              : t("dashboard.hours_left", { hours: Math.max(0, target - hours).toFixed(1) })}
          </p>
          <p className="text-[11px] text-zinc-400">{t("dashboard.target_short", { target })}</p>
        </div>
      </div>
      <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
        <div
          className="h-full rounded-full bg-blue-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function WeekStrip({ start, dayMap, selectedDate, onSelect }) {
  const { daysShort } = useLocalizedDates();
  const todayIso = toIso(new Date());
  const weekHourValues = Array.from({ length: 7 }, (_, i) => {
    const iso = toIso(addDays(start, i));
    return dayMap.get(iso)?.hours || 0;
  });
  const maxH = Math.max(8, ...weekHourValues);
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] dark:bg-zinc-900">
      <div className="grid grid-cols-7">
        {Array.from({ length: 7 }, (_, i) => {
          const d = addDays(start, i);
          const iso = toIso(d);
          const dd = dayMap.get(iso);
          const has = !!dd && dd.shifts.length > 0;
          const isSelected = iso === selectedDate;
          const isToday = iso === todayIso;
          const barPct = has ? (dd.hours / maxH) * 100 : 0;
          return (
            <button
              key={iso}
              type="button"
              data-testid={`week-cell-${iso}`}
              onClick={() => onSelect(iso)}
              className="group relative flex flex-col items-center gap-1.5 px-1 py-4 transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/40"
            >
              <span className={"text-[11px] font-medium " + (isToday ? "text-blue-600 dark:text-blue-400" : "text-zinc-400")}>
                {daysShort[d.getDay()]}
              </span>
              <span
                className={
                  "grid h-9 w-9 place-items-center rounded-full font-mono text-[14px] font-semibold tabular-nums transition-colors " +
                  (isSelected
                    ? "bg-blue-500 text-white shadow-[0_4px_12px_-4px_rgba(59,130,246,0.45)]"
                    : isToday
                    ? "text-blue-600 dark:text-blue-400"
                    : "text-zinc-700 dark:text-zinc-300")
                }
              >
                {d.getDate()}
              </span>

              <div className="h-1 w-6 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                {has && (
                  <div
                    className={"h-full rounded-full " + (isSelected ? "bg-blue-500" : "bg-zinc-400 dark:bg-zinc-600")}
                    style={{ width: `${barPct}%` }}
                  />
                )}
              </div>
              <span className={"text-[10.5px] font-medium tabular-nums " + (has ? "text-zinc-500" : "text-zinc-300 dark:text-zinc-700")}>
                {has ? `${dd.hours}h` : "·"}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SelectedDayCard({ date, day }) {
  const { t } = useTranslation();
  const { days, months } = useLocalizedDates();
  const d = new Date(date + "T00:00:00");
  const isToday = date === toIso(new Date());
  return (
    <div className="rounded-2xl bg-white p-6 shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] dark:bg-zinc-900">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-[13px] text-zinc-500">
            {months[d.getMonth()]} {d.getDate()}.
          </p>
          <p className="mt-1 text-[22px] font-semibold tracking-tight">
            {days[d.getDay()]}
            {isToday && (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 align-middle text-[11px] font-medium text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                {t("common.today")}
              </span>
            )}
          </p>
        </div>
        {day && day.shifts.length > 0 && (
          <p className="font-mono text-[28px] font-semibold tabular-nums leading-none tracking-tight">
            {day.hours}h
          </p>
        )}
      </div>

      <div className="mt-5 space-y-2">
        {!day || day.shifts.length === 0 ? (
          <p className="rounded-xl bg-zinc-50 py-6 text-center text-[13px] text-zinc-400 dark:bg-zinc-800/40">
            {t("schedule.no_shift")}
          </p>
        ) : (
          day.shifts.map((s) => <ShiftRow key={s.id} shift={s} />)
        )}
      </div>
    </div>
  );
}

function ShiftRow({ shift }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl bg-zinc-50 p-4 dark:bg-zinc-800/40">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-end gap-2">
          <span className="font-mono text-[20px] font-semibold tabular-nums leading-none">{shift.start_time}</span>
          <ArrowRight className="mb-1 h-3.5 w-3.5 text-zinc-400" strokeWidth={2} />
          <span className="font-mono text-[16px] font-medium tabular-nums leading-none text-zinc-500">{shift.end_time}</span>
        </div>
        <span className="font-mono text-[15px] font-semibold tabular-nums">{shift.hours}h</span>
      </div>
      {shift.site_name && (
        <p className="mt-2 inline-flex items-center gap-1 text-[12px] text-zinc-500">
          <MapPin className="h-3 w-3" strokeWidth={2} />
          {shift.site_name}
        </p>
      )}
      {shift.unpaid_break_minutes > 0 && (
        <p className="mt-1 text-[11px] text-zinc-400">
          {t("dashboard.unpaid_break", { count: shift.unpaid_break_minutes })}
        </p>
      )}
    </div>
  );
}

function UpcomingDayRow({ day, selected, onClick, last }) {
  const { days } = useLocalizedDates();
  const d = new Date(day.date + "T00:00:00");
  const todayIso = toIso(new Date());
  const isToday = day.date === todayIso;
  return (
    <>
      <button
        type="button"
        onClick={onClick}
        data-testid={`upcoming-day-${day.date}`}
        className={
          "flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors " +
          (selected
            ? "bg-blue-50/60 dark:bg-blue-950/20"
            : "hover:bg-zinc-50 dark:hover:bg-zinc-800/30")
        }
      >
        <div
          className={
            "grid h-10 w-10 shrink-0 place-items-center rounded-2xl font-mono text-[13px] font-semibold tabular-nums " +
            (isToday
              ? "bg-blue-500 text-white"
              : "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300")
          }
        >
          {d.getDate()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-medium tracking-tight">
            {days[d.getDay()]}
          </p>
          <p className="mt-0.5 truncate text-[11.5px] text-zinc-500">
            {day.shifts.map((s) => `${s.start_time}–${s.end_time}`).join(" · ")}
          </p>
        </div>
        <span className="shrink-0 font-mono text-[14px] font-semibold tabular-nums">{day.hours}h</span>
      </button>
      {!last && <div className="mx-5 h-px bg-zinc-100 dark:bg-zinc-800" />}
    </>
  );
}
